import { useCallback } from "react";

import { Button } from "@newsela/angelou";
import { withTrackEvent, objectTypes, actionNames } from "@newsela/snitch";
import { STANDARDS_SET_MODE } from "@/search/NewSearchPageMVP/types/IStandardsSetMode";
import { useStandardsModalContext } from "@/search/NewSearchPageMVP/provider/StandardsModalProvider";
import { useStandardsSearchContext } from "@/search/NewSearchPageMVP/provider/StandardsSearchProvider";
import { StandardsLoadingSpinner } from "../shared/StandardsLoadingSpinner";
import {
  buttonCss,
  footerButtonsCss,
  primaryButtonsContainerCSS,
} from "./StandardsModalFooter.styles";
import { useStandardsModalFooter } from "./hooks";

const ButtonWithTrackEvent = withTrackEvent(Button);

interface StandardsModalFooterProps {
  setStandardsModalActive: (val: boolean) => void;
}

export const StandardsModalFooter = ({
  setStandardsModalActive,
}: StandardsModalFooterProps) => {
  const {
    getters: {
      confirmModalData: { editData },
      newStandardsSet,
      standardsSetMode,
      isDeletingStandardsSet,
    },
    handlers: {
      addNewStandardsSet,
      editStandardsSet,
      setAllowModalTabAutoChanges,
      openConfirmModal,
    },
  } = useStandardsModalContext();

  const isEditMode = standardsSetMode === STANDARDS_SET_MODE.EDIT;

  const { resetStandardsSearch } = useStandardsSearchContext();

  const {
    dataQaSelectors,
    getCancelEventProperties,
    getEventProperties,
    confirmButtonText,
    handleApplyFilters,
    isConfirmButtonDisabled,
    confirmButtonLoading,
  } = useStandardsModalFooter(setStandardsModalActive);

  const handleCancel = () => {
    resetStandardsSearch();
    setAllowModalTabAutoChanges(false);
    setStandardsModalActive(false);
  };

  const handleConfirm = () => {
    resetStandardsSearch();
    setAllowModalTabAutoChanges(true);
    if (standardsSetMode === STANDARDS_SET_MODE.VIEW) {
      handleApplyFilters();
    } else {
      addNewStandardsSet(newStandardsSet);
    }
  };

  const handleUpdateStandardsSet = useCallback(() => {
    if (editData) {
      editStandardsSet(editData);
    }
  }, [editData]);

  const handleClick = () => {
    if (isEditMode) {
      handleUpdateStandardsSet();
    } else {
      handleConfirm();
    }
  };

  return (
    <div css={footerButtonsCss}>
      {isEditMode && (
        <StandardsLoadingSpinner
          waitForAPI
          isButtonSpinner
          apiFlag={isDeletingStandardsSet}
        >
          <ButtonWithTrackEvent
            __cssFor={buttonCss}
            flavor={Button.flavors.danger}
            onClick={() => openConfirmModal(STANDARDS_SET_MODE.DELETE)}
            objectType={objectTypes.content}
            actionName={actionNames.delete}
            streamProperties={{
              description: "Delete standards set",
            }}
            dataProps={{
              "data-qa-selector": "delete-standard-set-button",
            }}
          >
            Delete Set
          </ButtonWithTrackEvent>
        </StandardsLoadingSpinner>
      )}
      <div css={primaryButtonsContainerCSS}>
        <ButtonWithTrackEvent
          __cssFor={buttonCss}
          dataProps={{ "data-qa-selector": dataQaSelectors.cancel }}
          onClick={handleCancel}
          flavor={Button.flavors.secondary}
          {...getCancelEventProperties()}
        >
          Cancel
        </ButtonWithTrackEvent>
        <StandardsLoadingSpinner
          waitForAPI
          apiFlag={confirmButtonLoading[standardsSetMode]}
          isButtonSpinner
        >
          <ButtonWithTrackEvent
            __cssFor={buttonCss}
            dataProps={{ "data-qa-selector": dataQaSelectors.confirm }}
            onClick={handleClick}
            flavor={Button.flavors.primary}
            disabled={isConfirmButtonDisabled[standardsSetMode]}
            {...getEventProperties(standardsSetMode)}
          >
            {confirmButtonText}
          </ButtonWithTrackEvent>
        </StandardsLoadingSpinner>
      </div>
    </div>
  );
};
